/**
 * fichiers.js — ouvrir un fichier servi derrière un jeton
 *
 * Les routes de téléchargement du backend — PDF de compte rendu, devis,
 * DOSTEC — exigent un en-tête `Authorization`. Un simple `<a href>` ne
 * l'envoie pas : le lien répondait « Token manquant » et la fiche ne pouvait
 * pas ouvrir un CRI. On récupère donc le fichier avec le jeton, et on l'ouvre
 * depuis un blob.
 *
 * L'onglet est ouvert AVANT l'attente : ouvert après, le navigateur le prend
 * pour une fenêtre surgissante et le bloque.
 */

const jeton = () => localStorage.getItem('admin_token');

/**
 * Le nom de fichier que le serveur annonce, et s'il demande un téléchargement.
 *
 * Nos routes rendent « attachment; filename="CRI_VALEA_FAILED_O26…_CLIENT.pdf" ».
 * En passant par un blob, cet en-tête était perdu : le visualiseur de Chrome
 * n'avait plus de nom à proposer et enregistrait « document.pdf ». On le relit
 * donc, et on respecte ce que le serveur demande.
 */
function dispositionDe(res, url) {
  const brut = res.headers.get('content-disposition') || '';
  const guillemets = brut.match(/filename\*?=(?:UTF-8'')?"([^"]+)"/i);
  const nu = brut.match(/filename\*?=(?:UTF-8'')?([^;]+)/i);
  let nom = (guillemets && guillemets[1]) || (nu && nu[1].trim()) || '';
  try { nom = decodeURIComponent(nom); } catch { /* nom déjà lisible */ }
  if (!nom) nom = String(url).split('/').pop().split('?')[0];
  return { nom, telecharger: /^\s*attachment/i.test(brut) };
}

/** `true` si l'adresse passe par notre API, donc exige le jeton. */
export function protege(url) {
  const u = String(url || '');
  return u.startsWith('/api/') || u.startsWith('api/');
}

/**
 * Ouvre le fichier dans un onglet, ou le télécharge si le navigateur ne sait
 * pas l'afficher. Rend une erreur lisible plutôt qu'un onglet blanc.
 */
export async function ouvrirFichierProtege(url, nom) {
  if (!url) return;
  if (!protege(url)) { window.open(url, '_blank', 'noopener'); return; }

  const onglet = window.open('', '_blank');
  try {
    // `v` contourne le cache : un PDF régénéré doit être celui qu'on voit.
    const sep = url.includes('?') ? '&' : '?';
    const res = await fetch(`${url}${sep}v=${Date.now()}`, {
      headers: { Authorization: `Bearer ${jeton()}` },
    });
    if (!res.ok) {
      let message = `Erreur ${res.status}`;
      try { message = (await res.json()).error || message; } catch { /* corps non JSON */ }
      throw new Error(message);
    }
    const { nom: nomServeur, telecharger } = dispositionDe(res, url);
    const nomFinal = nom || nomServeur;

    const blob = await res.blob();
    // Un File plutôt qu'un Blob : il porte un nom, que le visualiseur de
    // certains navigateurs reprend quand on enregistre depuis l'onglet.
    const fichier = new File([blob], nomFinal, { type: blob.type });
    const lien = URL.createObjectURL(fichier);

    if (telecharger || !onglet) {
      // Le serveur demande un téléchargement — ou l'onglet a été bloqué. Dans
      // les deux cas on enregistre, avec le nom que le serveur a donné.
      if (onglet) onglet.close();
      const a = document.createElement('a');
      a.href = lien;
      a.download = nomFinal;
      a.click();
      setTimeout(() => URL.revokeObjectURL(lien), 15000);
    } else {
      onglet.location = lien;
      // Le blob doit survivre au temps que l'onglet le charge.
      setTimeout(() => URL.revokeObjectURL(lien), 60000);
    }
  } catch (e) {
    if (onglet) onglet.close();
    window.alert('Ouverture impossible : ' + (e.message || 'erreur inconnue'));
  }
}

/** Un lien qui se comporte comme un lien, mais passe le jeton. */
export function proprietesLien(url, nom) {
  return {
    href: url || '#',
    onClick: e => {
      if (!protege(url)) return;
      e.preventDefault();
      ouvrirFichierProtege(url, nom);
    },
    target: '_blank',
    rel: 'noreferrer',
  };
}
